import React, { useRef, useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CustomDatePicker = ({ selected, onChange, onCalendarClick, minDate, placeholderText = 'Calendar' }) => {
  const datePickerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleDateChange = (date) => {
    onChange(date);
    setIsOpen(false); // Close the date picker when a date is selected
  };

  const handleCalendarClick = () => {
    onChange(null); // Clear the date field
    setIsOpen(false); // Close the date picker
    onCalendarClick(); // Switch to calendar view
  };

  const handleTodayClick = () => {
    const today = new Date();
    onChange(today); // Set today's date - the picker will automatically update
    setIsOpen(false); // Close the date picker
  };

  // Handle click outside to close the date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayText = () => {
    if (!selected) return placeholderText;
    const today = new Date();
    const isToday = selected.toDateString() === today.toDateString();
    if (isToday) return 'Today';
    return selected.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <div className="relative" ref={datePickerRef}>
      <div
        className="block h-8 w-full px-3 sm:px-4 py-1 text-slate-600 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition text-sm cursor-pointer flex items-center"
        onClick={() => setIsOpen(true)}
      >
        {getDisplayText()}
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 z-[9999] bg-white border border-slate-200 rounded-lg shadow-lg">
          <div className="p-3 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between">
              {/* Calendar Button */}
              <button
                type="button"
                onClick={handleCalendarClick}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Calendar View
              </button>

              {/* Today Button */}
              <button
                type="button"
                onClick={handleTodayClick}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Today
              </button>
            </div>
          </div>
          
          <div className="[&_.react-datepicker]:!bg-white [&_.react-datepicker]:!border-none [&_.react-datepicker__header]:!bg-white [&_.react-datepicker__header]:!border-none [&_.react-datepicker__month-container]:!bg-white">
            <DatePicker
              selected={selected}
              onChange={handleDateChange}
              inline
              minDate={minDate}
              dayClassName={(date) => {
                const today = new Date();
                const isToday = date.toDateString() === today.toDateString();
                const isSelected = selected && date.toDateString() === selected.toDateString();
                const isPast = date < today.setHours(0, 0, 0, 0);
                
                let className = 'w-8 h-8 flex items-center justify-center rounded-full transition-colors text-xs font-medium';
                
                if (isPast) {
                  className += ' text-slate-600 cursor-not-allowed';
                } else if (isToday) {
                  className += ' font-semibold text-blue-600 border border-blue-300 bg-blue-50';
                } else if (isSelected) {
                  className += ' bg-blue-600 text-white hover:bg-blue-700';
                } else {
                  className += ' text-slate-600 hover:bg-blue-50 hover:text-blue-600';
                }
                
                return className;
              }}
              weekDayClassName={() => 'text-blue-600 text-xs font-semibold py-1 text-center'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;